// Copiado y tipado desde web/Clock_Web.html — NO modificar la lógica.

const MONTHS_ES = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'] as const;
const MONTHS_EN = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'] as const;
const MONTHS_ES_LONG = ['enero','febrero','marzo','abril','mayo','junio','julio','agosto','septiembre','octubre','noviembre','diciembre'] as const;
const MONTHS_EN_LONG = ['January','February','March','April','May','June','July','August','September','October','November','December'] as const;

/** Zero-pads a number to the given width. pad(9) → '09', pad(5, 3) → '005' */
export function pad(n: number, w = 2): string {
  return String(n).padStart(w, '0');
}

/** Returns an ISO date string: '2026-04-29' */
export function isoDate(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Formats a Date as 'DD Mon YYYY': '29 Abr 2026' */
export function formatDate(d: Date): string {
  return `${d.getDate()} ${MONTHS_ES[d.getMonth()] ?? ''} ${d.getFullYear()}`;
}

/** Formats a Date as "<Weekday>, <D> <MMM> <YYYY>" with capitalized weekday and 3-letter month.
 *
 *  Spanish example: "Viernes, 5 Jun 2026"
 *  English example: "Friday, 5 Jun 2026"
 *
 *  The weekday name comes from `Intl.DateTimeFormat(locale, { weekday: 'long' })`,
 *  with the first letter capitalized (Spanish `Intl` default returns lowercase).
 *  Month abbreviations are static 3-letter tables (MONTHS_ES / MONTHS_EN) to keep
 *  the output stable across runtimes.
 */
export function formatDateWithWeekday(d: Date, locale: 'es' | 'en' = 'es'): string {
  const intlLocale = locale === 'en' ? 'en-US' : 'es-ES';
  const weekdayRaw = new Intl.DateTimeFormat(intlLocale, { weekday: 'long' }).format(d);
  const weekday = weekdayRaw.charAt(0).toUpperCase() + weekdayRaw.slice(1);
  const months = locale === 'en' ? MONTHS_EN : MONTHS_ES;
  const month = months[d.getMonth()] ?? '';
  return `${weekday}, ${d.getDate()} ${month} ${d.getFullYear()}`;
}

export function formatDateNaturalLong(d: Date, locale: 'es' | 'en' = 'es'): string {
  const intlLocale = locale === 'en' ? 'en-US' : 'es-ES';
  const weekdayRaw = new Intl.DateTimeFormat(intlLocale, { weekday: 'long' }).format(d);
  const weekday = weekdayRaw.charAt(0).toUpperCase() + weekdayRaw.slice(1);
  if (locale === 'en') {
    const monthEn = MONTHS_EN_LONG[d.getMonth()] ?? '';
    return `${weekday}, ${monthEn} ${d.getDate()}, ${d.getFullYear()}`;
  }
  const monthEsRaw = MONTHS_ES_LONG[d.getMonth()] ?? '';
  const monthEs = monthEsRaw.charAt(0).toUpperCase() + monthEsRaw.slice(1);
  return `${weekday} ${d.getDate()} De ${monthEs}, ${d.getFullYear()}`;
}

export function formatTimeWithSeconds(d: Date): string {
  const h = d.getHours();
  const m = d.getMinutes();
  const s = d.getSeconds();
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${pad(h12)}:${pad(m)}:${pad(s)} ${ampm}`;
}

export function formatTimeFull(d: Date): string {
  const h = d.getHours();
  const m = d.getMinutes();
  const ampm = h >= 12 ? 'pm' : 'am';
  const h12 = h % 12 || 12;
  return `${pad(h12)}:${pad(m)} ${ampm}`;
}

export interface TimeParts {
  h: string;    // zero-padded hours (12-hour)
  m: string;    // zero-padded minutes
  s: string;    // zero-padded seconds
  ampm: string; // 'AM' | 'PM'
}

/** Splits a Date into { h, m, s, ampm } strings for the clock display. */
export function getTimeParts(d: Date): TimeParts {
  const h  = d.getHours();
  const m  = d.getMinutes();
  const s  = d.getSeconds();
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12  = h % 12 || 12;
  return { h: pad(h12), m: pad(m), s: pad(s), ampm };
}

/** Formats a Date as "miércoles, 6 de mayo" using Intl.DateTimeFormat.
 * Returns "—" for invalid dates. */
export function formatDayLabel(date: Date): string {
  if (isNaN(date.getTime())) {
    return '—';
  }
  const formatter = new Intl.DateTimeFormat('es-ES', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
  return formatter.format(date);
}

/** Formats a Date as 'dd/mm/yyyy': '12/05/2026' (admin general timelog rows). */
export function formatDdMmYyyy(d: Date): string {
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
}

/** Returns a { from, to } ISO date range covering the last 30 days. */
export function defaultRange(): { from: string; to: string } {
  const to   = new Date();
  const from = new Date(to);
  from.setDate(from.getDate() - 30);
  return { from: isoDate(from), to: isoDate(to) };
}

/** Parses the backend's PostgreSQL timestamp string into a JS Date (local). */
export function parsePgDateTime(pgStr: string): Date {
  // API returns "YYYY-MM-DD HH:mm:ss" without TZ indicator — treat as local
  return new Date(pgStr.replace(' ', 'T'));
}

/** Parses the backend's UTC timestamp (datetime_pg) into a JS Date (UTC). */
export function parsePgDateTimeUtc(pgStr: string): Date {
  return new Date(`${pgStr.replace(' ', 'T')}Z`);
}
